import {
  Badge,
  ErrorBoundary,
  Modal,
  ModalBody,
  ModalHead,
} from '@dixa/lib-shared-habla';
import produce from 'immer';
import type {
  InstrumentPanel,
  ReadInstrumentsQueryFn,
} from 'lib-analytics-types';
import { isResponseWithData } from 'lib-shared-auth';
import omit from 'lodash/omit';
import { useCallback, useEffect, useMemo } from 'react';

import { analyticsApi } from 'apis/analytics';
import { appActions } from 'app/app.actions';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

import { useAnalyticsState } from '../../../../utils/useAnalyticsState/useAnalyticsState';
import { usePersistInstrumentSettings } from '../../utils/usePersistInstrumentSettings';
import { InstrumentPanelTitle } from '../panel/components/title/instrumentPanelTitle';
import { InstrumentRendererError } from '../renderer/components/error/instrumentRendererError';
import { InstrumentRenderer } from '../renderer/instrumentRenderer';

import styles from './instrumentModal.module.scss';

type InstrumentModalProps = {
  /** The instrument panel. */
  panel: InstrumentPanel;
  /** Called when the modal is closed. */
  onClose: () => void;
};

/**
 * Represents the modal to fetch and display in-depth instruments for a panel,
 * which is a way to provide more detailed data for a specific panel on-demand.
 *
 * The in-depth instruments are requested with the `in_depth: true` flag in the
 * request `meta` arg, as well as all of the same `controls` as the original
 * panel, with the exception of some specific settings that are only relevant to
 * the original panel, and should not be applied to the in-depth instruments.
 */
export const InstrumentModal = ({
  panel: initialPanel,
  onClose,
}: InstrumentModalProps): JSX.Element => {
  const panel = useMemo(
    () =>
      produce(initialPanel, (draftPanel) => {
        if (draftPanel.controls) {
          // The original panel may have instrument settings that should not be applied to the
          // in-depth instruments, so we remove them from the panel's settings controls.
          draftPanel.controls.settings = omit(draftPanel.controls.settings, [
            'granularity',
            'visualization',
          ]);
        }
      }),
    [initialPanel],
  );

  const dispatch = useAppDispatch();
  const { context, period, filters, settings } = useAnalyticsState(panel);

  const { instrumentSettings, setInstrumentSetting } =
    usePersistInstrumentSettings();

  const [readInstrumentsQuery, { instruments, isFetching, isError }] =
    analyticsApi.useLazyReadInstrumentsQuery({
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        instruments: data?.data.instruments ?? [],
      }),
    });

  const readInstruments: ReadInstrumentsQueryFn = useCallback(
    async (arg) => {
      const response = await readInstrumentsQuery({
        id: panel.id,
        context,
        period,
        filters,
        settings: { ...settings, ...instrumentSettings },
        meta: {
          ...arg?.meta,
          in_depth: true,
        },
      });

      if (isResponseWithData(response)) {
        return response.data;
      }
    },
    [
      context,
      filters,
      instrumentSettings,
      panel.id,
      period,
      readInstrumentsQuery,
      settings,
    ],
  );

  /**
   * Fetches the in-depth instrument data:
   *
   * - On initial component mount.
   * -
   * - When the `instrumentSettings` change. This happens when the user changes a
   *   setting specific to an instrument, such as chart granularity.
   */
  useEffect(() => {
    readInstruments();

    // Only run this effect when the relevant data points that should trigger an API request change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instrumentSettings]);

  const handleExport = (): void => {
    dispatch(
      appActions.showModal({
        modal: {
          type: 'analytics:export',
          arg: {
            instruments,
            panel,
          },
        },
      }),
    );
  };

  return (
    <Modal className={styles.modal} isOpen onClose={onClose}>
      <ModalHead
        title={
          <InstrumentPanelTitle
            panel={panel}
            prefix={
              <Badge variant="primary" size="medium">
                In-depth
              </Badge>
            }
            data-analytics-panel={panel.id}
          />
        }
        secondaryButton={
          panel.exportable
            ? {
                icon: 'download',
                tooltipContent: 'Export',
                onClick: handleExport,
              }
            : undefined
        }
        onClose={onClose}
      />

      <ModalBody className={styles.modalBody}>
        <ErrorBoundary fallback={<InstrumentRendererError />}>
          <InstrumentRenderer
            panel={panel}
            instruments={instruments}
            isFetching={isFetching}
            isError={isError}
            onReadInstruments={readInstruments}
            onSetInstrumentSetting={setInstrumentSetting}
          />
        </ErrorBoundary>
      </ModalBody>
    </Modal>
  );
};
